import React, { useState, useEffect, useMemo, useContext, useCallback, useRef } from 'react';
import {
    Typography, Box, CssBaseline, Tabs, Tab, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
    Chip, Avatar, Switch, IconButton, Tooltip, TextField, InputAdornment,
    Button, Slider,
} from '@mui/material';
import StopCircleIcon        from '@mui/icons-material/StopCircle';
import HeadphonesIcon        from '@mui/icons-material/Headphones';
import HeadsetOffIcon        from '@mui/icons-material/HeadsetOff';
import MicIcon               from '@mui/icons-material/Mic';
import MicOffIcon            from '@mui/icons-material/MicOff';
import VolumeUpIcon          from '@mui/icons-material/VolumeUp';
import VolumeOffIcon         from '@mui/icons-material/VolumeOff';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CloseIcon             from '@mui/icons-material/Close';
import axios from 'axios';
import { global_link, audio_socket_base_url, audio_socket_path } from '../Components/Globallink';
import { jwtDecode } from 'jwt-decode';
import WebSocketContext from '../Context/WebSocket';
import { io } from 'socket.io-client';

// ── Helpers ───────────────────────────────────────────────────────────────────
function getTimeDifference(start, end) {
    if (!start) return '—';
    const normalize = (s) => typeof s === 'string' ? s.replace(' ', 'T') : s;
    const startMs = typeof start === 'number' ? start : new Date(normalize(start)).getTime();
    const endMs   = end ? (typeof end === 'number' ? end : new Date(normalize(end)).getTime()) : Date.now();
    if (isNaN(startMs) || isNaN(endMs)) return '—';
    const d = Math.max(0, Math.floor((endMs - startMs) / 1000));
    return `${String(Math.floor(d/3600)).padStart(2,'0')}:${String(Math.floor((d%3600)/60)).padStart(2,'0')}:${String(d%60).padStart(2,'0')}`;
}

function computeHistoryDuration(row) {
    if (!row.start_time || !row.end_time) return '—';
    const dateBase = (row.date || '').split('T')[0];
    if (!dateBase) return '—';
    const s = new Date(`${dateBase}T${row.start_time}`).getTime();
    const e = new Date(`${dateBase}T${row.end_time}`).getTime();
    if (isNaN(s) || isNaN(e)) return '—';
    return getTimeDifference(s, e);
}

function formatSeconds(secs) {
    if (secs == null || isNaN(secs)) return '—';
    const s = Math.max(0, Math.floor(secs));
    return `${String(Math.floor(s/3600)).padStart(2,'0')}:${String(Math.floor((s%3600)/60)).padStart(2,'0')}:${String(s%60).padStart(2,'0')}`;
}

const STALE_QUEUE_MS = 30 * 60 * 1000;
const api = axios.create({ baseURL: global_link });

// ─────────────────────────────────────────────────────────────────────────────
const Monitor = () => {
    const authToken = process.env.AUTH_TOKEN;
    const { socket } = useContext(WebSocketContext);

    const [activeTab, setActiveTab]                     = useState(0);
    const [onlineUsers, setOnlineUsers]                 = useState([]);
    const [presencePage, setPresencePage]               = useState(0);
    const [presenceRowsPerPage, setPresenceRowsPerPage] = useState(10);
    const [calls, setCalls]                             = useState({});
    const [callPage, setCallPage]                       = useState(0);
    const [callRowsPerPage, setCallRowsPerPage]         = useState(5);

    const [queue, setQueue]                             = useState([]);
    const [queuePage, setQueuePage]                     = useState(0);
    const [queueRowsPerPage, setQueueRowsPerPage]       = useState(10);

    const [activeBreaks, setActiveBreaks]               = useState([]);
    const [breakHistory, setBreakHistory]               = useState([]);
    const [breakTotalItems, setBreakTotalItems]         = useState(0);
    const [searchBreakTerm, setSearchBreakTerm]         = useState('');
    const [breakPage, setBreakPage]                     = useState(0);
    const [breakRowsPerPage, setBreakRowsPerPage]       = useState(10);
    const [endingBreaks, setEndingBreaks]               = useState(new Set());
    const [activeEnabled, setActiveEnabled]             = useState({});
    const [tick, setTick]                               = useState(0);
    const fetchBreakDataRef                             = useRef(null);

    const audioSocketRef                                = useRef(null);
    const [listeningCallId, setListeningCallId]         = useState(null);
    const listeningCallIdRef                            = useRef(null);
    const [isWhispering, setIsWhispering]               = useState(false);
    const [audioConnected, setAudioConnected]           = useState(false);
    const audioConnectedRef                             = useRef(false);
    const [audioErrors, setAudioErrors]                 = useState(0);
    const whisperRecorderRef                            = useRef(null);
    const [autoplayBlocked, setAutoplayBlocked]         = useState(false);
    
    // Audio context for volume control
    const supervisorAudioCtxRef                         = useRef(null);
    const gainNodeRef                                   = useRef(null);
    const [volume, setVolume]                           = useState(1);
    const [isMuted, setIsMuted]                         = useState(false);
    const [audioLevel, setAudioLevel]                   = useState(0);
    const levelAnimRef                                  = useRef(null);

    // ── MSE streaming refs (for listen-in audio) ──────────────────────────────
    const mediaSourceRef    = useRef(null);
    const sourceBufferRef   = useRef(null);
    const remoteAudioRef    = useRef(null);
    const chunkQueueRef     = useRef([]);
    const mseReadyRef       = useRef(false);
    const mimeTypeRef       = useRef('audio/webm; codecs=opus');
    const analyserRef       = useRef(null);
    const headerReceivedRef = useRef(false);

    useEffect(() => { listeningCallIdRef.current = listeningCallId; }, [listeningCallId]);

    const callList = useMemo(() => Object.values(calls), [calls]);

    // ── Setup dedicated audio element ────────────────────────────────────────
    useEffect(() => {
        let remoteAudio = document.getElementById('monitor-remote-audio');
        if (!remoteAudio) {
            remoteAudio = document.createElement('audio');
            remoteAudio.id = 'monitor-remote-audio';
            remoteAudio.autoplay = true;
            remoteAudio.style.display = 'none';
            document.body.appendChild(remoteAudio);
        }
        remoteAudioRef.current = remoteAudio;
        
        return () => {
            if (remoteAudioRef.current) {
                remoteAudioRef.current.pause();
                if (remoteAudioRef.current.src?.startsWith('blob:')) {
                    URL.revokeObjectURL(remoteAudioRef.current.src);
                }
                remoteAudioRef.current.src = '';
            }
        };
    }, []);

    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            const cutoff = Date.now() - STALE_QUEUE_MS;
            setQueue(prev => {
                const next = prev.filter(c => {
                    const j = new Date(c.joinTime).getTime();
                    return !isNaN(j) && j >= cutoff;
                });
                return next.length === prev.length ? prev : next;
            });
        }, 60_000);
        return () => clearInterval(id);
    }, []);

    // ── Level meter (for MSE audio element) ───────────────────────────────────
    const startLevelMeter = useCallback(() => {
        const audio = remoteAudioRef.current;
        if (!audio) return;
        
        if (!analyserRef.current && supervisorAudioCtxRef.current) {
            const ctx = supervisorAudioCtxRef.current;
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            
            try {
                const source = ctx.createMediaElementSource(audio);
                if (gainNodeRef.current) {
                    source.connect(gainNodeRef.current);
                } else {
                    const gain = ctx.createGain();
                    gain.gain.value = isMuted ? 0 : volume;
                    gainNodeRef.current = gain;
                    source.connect(gain);
                    gain.connect(analyser);
                    analyser.connect(ctx.destination);
                }
                analyserRef.current = analyser;
            } catch (e) {
                console.debug('[Audio] Could not connect to Web Audio for level meter:', e);
                return;
            }
        }
        
        const analyser = analyserRef.current;
        if (!analyser) return;
        
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
            analyser.getByteFrequencyData(data);
            const level = Math.round((data.reduce((a, b) => a + b, 0) / data.length / 255) * 100);
            setAudioLevel(level);
            levelAnimRef.current = requestAnimationFrame(tick);
        };
        levelAnimRef.current = requestAnimationFrame(tick);
    }, [volume, isMuted]);

    const stopLevelMeter = useCallback(() => {
        if (levelAnimRef.current) {
            cancelAnimationFrame(levelAnimRef.current);
            levelAnimRef.current = null;
        }
        setAudioLevel(0);
        analyserRef.current = null;
    }, []);

    // ── Drain queued chunks into SourceBuffer ──────────────────────────────
    const drainQueue = useCallback(() => {
        const sb = sourceBufferRef.current;
        if (!sb) return;
        if (sb.updating) return;
        if (chunkQueueRef.current.length === 0) return;
        if (!mseReadyRef.current) return;

        try {
            const nextChunk = chunkQueueRef.current.shift();
            if (nextChunk?.byteLength > 0) {
                sb.appendBuffer(nextChunk);
            }
        } catch (err) {
            console.warn('[Audio] SourceBuffer append error:', err.name, err.message);
            if (err.name === 'QuotaExceededError') {
                chunkQueueRef.current = [];
            }
        }
    }, []);

    // ── Teardown MSE pipeline ──────────────────────────────────────────────
    const teardownMSE = useCallback(() => {
        mseReadyRef.current = false;
        headerReceivedRef.current = false;
        chunkQueueRef.current = [];
        
        if (sourceBufferRef.current) {
            try {
                if (!sourceBufferRef.current.updating) {
                    sourceBufferRef.current.abort();
                }
            } catch (e) {}
            sourceBufferRef.current = null;
        }
        
        if (mediaSourceRef.current) {
            try {
                if (mediaSourceRef.current.readyState === 'open') {
                    mediaSourceRef.current.endOfStream();
                }
            } catch (e) {}
            mediaSourceRef.current = null;
        }
        
        if (remoteAudioRef.current && remoteAudioRef.current.src?.startsWith('blob:')) {
            URL.revokeObjectURL(remoteAudioRef.current.src);
            remoteAudioRef.current.src = '';
        }
        
        setAutoplayBlocked(false);
        console.log('[Audio] MSE pipeline torn down');
    }, []);

    // ── Handle incoming audio chunk with MSE ───────────────────────────────────
    const handleAudioChunk = useCallback(async (chunk, isHeader, mimeType, chunkIndex) => {
        let buf;
        if (chunk instanceof ArrayBuffer) {
            buf = chunk;
        } else if (ArrayBuffer.isView(chunk)) {
            buf = chunk.buffer.slice(chunk.byteOffset, chunk.byteOffset + chunk.byteLength);
        } else if (chunk && typeof chunk === 'object' && !Array.isArray(chunk)) {
            buf = new Uint8Array(Object.values(chunk)).buffer;
        } else {
            console.warn('[Audio] Unrecognised chunk type:', typeof chunk);
            return;
        }

        if (buf.byteLength < 10) return;

        const view = new Uint8Array(buf);
        const isWebMHeader = view[0] === 0x1a && view[1] === 0x45 && view[2] === 0xdf && view[3] === 0xa3;
        const isHeaderChunk = isHeader === true || isWebMHeader;
        
        console.log(`[Audio] Chunk ${chunkIndex}: isHeader=${isHeader}, isWebMHeader=${isWebMHeader}, size=${buf.byteLength}, headerReceived=${headerReceivedRef.current}`);

        if (isHeaderChunk && !headerReceivedRef.current) {
            console.log('[Audio] 🎯 PROCESSING HEADER CHUNK (once)');
            headerReceivedRef.current = true;
            
            // Don't call teardownMSE here - it clears everything we're about to set up
            // Just clean up the old source buffer if it exists
            if (sourceBufferRef.current) {
                try {
                    if (!sourceBufferRef.current.updating) {
                        sourceBufferRef.current.abort();
                    }
                } catch (e) {}
                sourceBufferRef.current = null;
            }
            
            if (mediaSourceRef.current && mediaSourceRef.current.readyState === 'open') {
                try {
                    mediaSourceRef.current.endOfStream();
                } catch (e) {}
                mediaSourceRef.current = null;
            }
            
            const ms = new MediaSource();
            mediaSourceRef.current = ms;
            
            const audio = remoteAudioRef.current;
            if (!audio) {
                console.error('[Audio] No audio element found');
                return;
            }
            
            audio.volume = isMuted ? 0 : volume;
            audio.muted = false;
            
            ms.addEventListener('sourceopen', () => {
                console.log('[Audio] SourceOpen event fired');
                try {
                    const mime = 'audio/webm; codecs=opus';
                    if (!MediaSource.isTypeSupported(mime)) {
                        console.error('[Audio] MIME type not supported:', mime);
                        return;
                    }
                    
                    const sb = ms.addSourceBuffer(mime);
                    sourceBufferRef.current = sb;
                    sb.mode = 'segments';
                    
                    sb.addEventListener('updateend', () => {
                        drainQueue();
                    });
                    
                    sb.addEventListener('error', (e) => {
                        console.error('[Audio] SourceBuffer error:', e);
                    });
                    
                    sb.appendBuffer(buf);
                    mseReadyRef.current = true;
                    
                    audio.play().then(() => {
                        console.log('[Audio] Playback started');
                        setAutoplayBlocked(false);
                    }).catch(e => {
                        console.warn('[Audio] Playback error:', e.message);
                        setAutoplayBlocked(true);
                        const playOnClick = () => {
                            audio.play().catch(e => console.warn(e));
                            document.removeEventListener('click', playOnClick);
                            document.removeEventListener('touchstart', playOnClick);
                        };
                        document.addEventListener('click', playOnClick, { once: true });
                        document.addEventListener('touchstart', playOnClick, { once: true });
                    });
                    
                } catch (err) {
                    console.error('[Audio] SourceOpen error:', err);
                    headerReceivedRef.current = false;
                }
            });
            
            ms.addEventListener('sourceended', () => console.log('[Audio] Source ended'));
            ms.addEventListener('sourceclose', () => console.log('[Audio] Source closed'));
            
            if (audio.src && audio.src.startsWith('blob:')) {
                URL.revokeObjectURL(audio.src);
            }
            audio.src = URL.createObjectURL(ms);
            return;
        }
        
        if (!headerReceivedRef.current) {
            chunkQueueRef.current.push(buf);
            console.log(`[Audio] Waiting for header, queued chunk ${chunkIndex}`);
            return;
        }
        
        if (!mseReadyRef.current || !sourceBufferRef.current) {
            chunkQueueRef.current.push(buf);
            console.log(`[Audio] Queued chunk ${chunkIndex}, queue size: ${chunkQueueRef.current.length}`);
            return;
        }
        
        const sb = sourceBufferRef.current;
        if (!sb.updating) {
            try {
                sb.appendBuffer(buf);
                console.log(`[Audio] Appended chunk ${chunkIndex}`);
            } catch (err) {
                console.warn(`[Audio] Append error for chunk ${chunkIndex}:`, err);
                chunkQueueRef.current.push(buf);
            }
        } else {
            chunkQueueRef.current.push(buf);
            console.log(`[Audio] Buffer updating, queued chunk ${chunkIndex}`);
        }
    }, [volume, isMuted, drainQueue, teardownMSE]);

    // ── Volume / mute controls for audio element ───────────────────────────────
    const handleVolumeChange = useCallback((_, v) => {
        setVolume(v);
        setIsMuted(false);
        const audio = remoteAudioRef.current;
        if (audio) audio.volume = v;
    }, []);

    const handleMuteToggle = useCallback(() => {
        const next = !isMuted;
        setIsMuted(next);
        const audio = remoteAudioRef.current;
        if (audio) audio.volume = next ? 0 : volume;
    }, [isMuted, volume]);

    // ── Whisper: Stop ─────────────────────────────────────────────────
    const stopWhisper = useCallback(() => {
        const recorder = whisperRecorderRef.current;
        if (recorder && recorder.state !== 'inactive') {
            try { recorder.stop(); } catch (e) {
                console.warn('[Whisper] Error stopping recorder:', e);
            }
        }
        if (recorder?.stream) {
            recorder.stream.getTracks().forEach(t => {
                if (t.readyState === 'live') t.stop();
            });
        }
        whisperRecorderRef.current = null;
        setIsWhispering(false);
        console.log('[Whisper] Stopped');
    }, []);

    // ── Stop listening ────────────────────────────────────────────────
    const stopListen = useCallback(() => {
        if (listeningCallIdRef.current && audioSocketRef.current?.connected) {
            audioSocketRef.current.emit('LEAVE_LISTEN', { callId: listeningCallIdRef.current });
        }
        stopWhisper();
        stopLevelMeter();
        teardownMSE();
        setListeningCallId(null);
        setAudioErrors(0);
        setAutoplayBlocked(false);
    }, [stopLevelMeter, stopWhisper, teardownMSE]);

    // ── Start listening ───────────────────────────────────────────────
    const startListen = useCallback((call) => {
        if (!audioSocketRef.current?.connected) {
            console.warn('[Audio] Cannot listen — socket not connected');
            setTimeout(() => {
                if (call && audioSocketRef.current?.connected) startListen(call);
            }, 1000);
            return;
        }

        if (listeningCallIdRef.current) {
            audioSocketRef.current.emit('LEAVE_LISTEN', { callId: listeningCallIdRef.current });
        }
        teardownMSE();

        const callIdToListen = String(call.callId);
        setListeningCallId(callIdToListen);

        console.log('[Audio] Attempting to join listen room:', callIdToListen);

        const joinTimeout = setTimeout(() => {
            console.warn('[Audio] Join listen timeout for call:', callIdToListen);
            if (listeningCallIdRef.current === callIdToListen) {
                setAudioErrors(prev => prev + 1);
            }
        }, 5000);

        const onJoinConfirmed = ({ callId }) => {
            if (callId === callIdToListen) {
                clearTimeout(joinTimeout);
                console.log('[Audio] Successfully joined listen room:', callId);
                setAudioErrors(0);
                startLevelMeter();
                audioSocketRef.current?.off('LISTEN_JOINED', onJoinConfirmed);
            }
        };

        audioSocketRef.current.on('LISTEN_JOINED', onJoinConfirmed);
        audioSocketRef.current.emit('JOIN_LISTEN', { callId: callIdToListen });

        return () => {
            clearTimeout(joinTimeout);
            audioSocketRef.current?.off('LISTEN_JOINED', onJoinConfirmed);
        };
    }, [startLevelMeter, teardownMSE]);

    // ── Whisper: Start ────────────────────────────────────────────────
    const startWhisper = useCallback(async () => {
        const callId = listeningCallIdRef.current;
        if (!callId || !audioSocketRef.current?.connected) {
            console.warn('[Whisper] Cannot start: no callId or socket disconnected');
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl:  true,
                },
                video: false,
            });

            const supportedMimeTypes = [
                'audio/webm; codecs=opus',
                'audio/webm',
                'audio/mp4; codecs=mp4a.40.2',
                'audio/mp4',
            ];
            let selectedMimeType = null;
            for (const mime of supportedMimeTypes) {
                if (MediaRecorder.isTypeSupported(mime)) {
                    selectedMimeType = mime;
                    console.log('[Whisper] Using MIME type:', mime);
                    break;
                }
            }
            if (!selectedMimeType) {
                console.error('[Whisper] No supported MIME type for MediaRecorder');
                stream.getTracks().forEach(t => t.stop());
                return;
            }

            const recorder = new MediaRecorder(stream, {
                mimeType:           selectedMimeType,
                audioBitsPerSecond: 32000,
            });

            let headerSent = false;
            let chunkCount = 0;

            recorder.ondataavailable = async (e) => {
                if (e.data.size < 10) return;
                try {
                    const buf = await e.data.arrayBuffer();
                    chunkCount++;
                    const isHeader = !headerSent;
                    if (isHeader) headerSent = true;

                    audioSocketRef.current?.emit('WHISPER_CHUNK', {
                        callId,
                        chunk:      buf,
                        isHeader,
                        mimeType:   selectedMimeType,
                        chunkIndex: chunkCount,
                        timestamp:  Date.now(),
                    });

                    if (chunkCount <= 3) {
                        console.log(`[Whisper] Sent chunk #${chunkCount}`, {
                            size:       buf.byteLength,
                            isHeader,
                            firstBytes: Array.from(new Uint8Array(buf).slice(0, 8))
                                .map(b => '0x' + b.toString(16).padStart(2, '0')).join(' '),
                        });
                    }
                } catch (err) {
                    console.error('[Whisper] Error processing chunk:', err);
                }
            };

            recorder.onerror = (e) => {
                console.error('[Whisper] MediaRecorder error:', e.error);
                stopWhisper();
            };

            recorder.onstop = () => {
                console.log('[Whisper] Recorder stopped');
                stream.getTracks().forEach(t => t.stop());
                headerSent = false;
                chunkCount = 0;
            };

            whisperRecorderRef.current = recorder;
            setIsWhispering(true);
            recorder.start(500);
            console.log('[Whisper] Started recording with 500ms chunks');

        } catch (err) {
            console.error('[Whisper] getUserMedia error:', err);
            if (err.name === 'NotAllowedError') {
                alert('Microphone access denied. Please enable microphone permissions to use whisper.');
            } else if (err.name === 'NotFoundError') {
                alert('No microphone found. Please connect a microphone to use whisper.');
            }
        }
    }, [stopWhisper]);

    // ── Audio socket setup ────────────────────────────────────────────
    useEffect(() => {
        const token = localStorage.getItem(authToken);
        if (!token) { console.warn('[Audio] No auth token'); return; }

        const audioSock = io(audio_socket_base_url, {
            path:                 audio_socket_path,
            auth:                 { token },
            transports:           ['websocket'],
            reconnection:         true,
            reconnectionAttempts: 10,
            reconnectionDelay:    1000,
            reconnectionDelayMax: 5000,
        });

        audioSocketRef.current = audioSock;

        if (audioSock.connected) {
            setAudioConnected(true);
            audioConnectedRef.current = true;
        }

        audioSock.on('connect', () => {
            console.log(`[Audio] Supervisor connected id:${audioSock.id}`);
            setAudioConnected(true);
            audioConnectedRef.current = true;
            setAudioErrors(0);
            if (listeningCallIdRef.current) {
                audioSock.emit('JOIN_LISTEN', { callId: listeningCallIdRef.current });
            }
        });

        audioSock.on('disconnect', (reason) => {
            console.warn('[Audio] Disconnected:', reason);
            setAudioConnected(false);
            audioConnectedRef.current = false;
            teardownMSE();
        });

        audioSock.on('connect_error', (err) => {
            console.error('[Audio] Connection error:', err.message);
            setAudioConnected(false);
            audioConnectedRef.current = false;
        });

        audioSock.on('LISTEN_ERROR', ({ message, callId }) => {
            console.warn('[Audio] Listen error:', message, callId);
            setListeningCallId(null);
            listeningCallIdRef.current = null;
            teardownMSE();
        });

        audioSock.on('LISTEN_JOINED', ({ callId }) => {
            console.log('[Audio] Joined listen room:', callId);
        });

        audioSock.on('CALL_ENDED_AUDIO', ({ callId }) => {
            if (listeningCallIdRef.current === callId) stopListen();
        });

        audioSock.on('AUDIO_CHUNK', ({ chunk, isHeader, mimeType, chunkIndex }) => {
            handleAudioChunk(chunk, isHeader, mimeType, chunkIndex);
        });

        return () => {
            audioSock.off('connect');
            audioSock.off('disconnect');
            audioSock.off('connect_error');
            audioSock.off('LISTEN_ERROR');
            audioSock.off('LISTEN_JOINED');
            audioSock.off('CALL_ENDED_AUDIO');
            audioSock.off('AUDIO_CHUNK');
            audioSock.disconnect();
            setAudioConnected(false);
            audioConnectedRef.current = false;
            teardownMSE();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authToken, handleAudioChunk, stopListen, teardownMSE]);

    // ── Socket connected-state sync ───────────────────────────────────
    useEffect(() => {
        const interval = setInterval(() => {
            const live = audioSocketRef.current?.connected ?? false;
            if (live !== audioConnectedRef.current) {
                console.log('[Audio] State sync correction — connected:', live);
                setAudioConnected(live);
                audioConnectedRef.current = live;
            }
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // ── Unmount cleanup ───────────────────────────────────────────────
    useEffect(() => {
        return () => {
            stopWhisper();
            stopLevelMeter();
            teardownMSE();
            if (supervisorAudioCtxRef.current?.state !== 'closed') {
                supervisorAudioCtxRef.current?.close();
            }
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Break data ────────────────────────────────────────────────────
    const fetchBreakData = useCallback(async () => {
        try {
            const authyToken = localStorage.getItem(authToken);
            const r = await api.get('/break/all', {
                params:  { search: searchBreakTerm, limit: breakRowsPerPage, offset: breakPage * breakRowsPerPage },
                headers: { Authorization: `Bearer ${authyToken}` },
            });
            setBreakHistory(r.data.data);
            setBreakTotalItems(r.data.total);
        } catch (e) { console.error('Error fetching break data:', e); }
    }, [searchBreakTerm, breakPage, breakRowsPerPage, authToken]);

    useEffect(() => { fetchBreakDataRef.current = fetchBreakData; }, [fetchBreakData]);
    useEffect(() => { fetchBreakData(); }, [fetchBreakData]);

    // ── Presence socket ───────────────────────────────────────────────
    useEffect(() => {
        if (!socket) return;
        if (socket.connected) socket.emit('REQUEST_SNAPSHOT');

        const fetchPresence = async () => {
            try {
                const authyToken = localStorage.getItem(authToken);
                const decoded = jwtDecode(authyToken);
                const r = await api.get(`/presence/${decoded.organization_id}`, {
                    headers: { Authorization: `Bearer ${authyToken}` },
                    withCredentials: true,
                });
                setOnlineUsers(r.data);
            } catch (e) { console.error('Presence fetch error:', e); }
        };
        fetchPresence();
        socket.on('connect', fetchPresence);

        const onSnapshot = (u) => setOnlineUsers(u);
        const onPresence = ({ userId, status, user_name, user_extension, user_level, connectedAt }) => {
            setOnlineUsers(prev => {
                if (status === 'online') {
                    if (prev.find(u => String(u.userId) === String(userId))) return prev;
                    return [...prev, {
                        userId:      String(userId),
                        status:      'online',
                        user_name:   user_name   || `User ${userId}`,
                        user_extension: user_extension || '—',
                        user_level:  user_level  || 'user',
                        connectedAt: connectedAt || new Date().toISOString(),
                    }];
                }
                return prev.filter(u => String(u.userId) !== String(userId));
            });
        };
        socket.on('PRESENCE_SNAPSHOT', onSnapshot);
        socket.on('USER_PRESENCE', onPresence);
        return () => {
            socket.off('PRESENCE_SNAPSHOT', onSnapshot);
            socket.off('USER_PRESENCE', onPresence);
            socket.off('connect', fetchPresence);
        };
    }, [socket, authToken]);

    // ── Breaks socket ─────────────────────────────────────────────────
    useEffect(() => {
        if (!socket) return;
        const fetchActiveBreaks = async () => {
            try {
                const authyToken = localStorage.getItem(authToken);
                const decoded = jwtDecode(authyToken);
                const r = await api.get(`/breaks/${decoded.organization_id}`, {
                    headers: { Authorization: `Bearer ${authyToken}` },
                    withCredentials: true,
                });
                setActiveBreaks(r.data);
            } catch (e) { console.error('Breaks fetch error:', e); }
        };
        fetchActiveBreaks();
        socket.on('connect', fetchActiveBreaks);

        const onBreaksSnapshot = (b) => setActiveBreaks(b);
        const onBreak = ({ userId, status, breakType, breakId, startTime, user_name, user_extension, user_level }) => {
            if (status === 'on_break') {
                setActiveBreaks(prev => {
                    if (prev.find(u => String(u.userId) === String(userId))) return prev;
                    return [...prev, {
                        userId:    String(userId),
                        status:    'on_break',
                        breakType: breakType || 'General Break',
                        breakId,
                        startTime: startTime || new Date().toISOString(),
                        user_name: user_name || `User ${userId}`,
                        user_extension: user_extension || '—',
                        user_level: user_level || 'user',
                    }];
                });
                setTimeout(() => fetchBreakDataRef.current?.(), 500);
            } else {
                setActiveBreaks(prev => prev.filter(u => String(u.userId) !== String(userId)));
                setEndingBreaks(prev => { const n = new Set(prev); n.delete(breakId); return n; });
                setTimeout(() => fetchBreakDataRef.current?.(), 500);
            }
        };
        const onBreakUpdate = () => fetchBreakDataRef.current?.();
        socket.on('BREAKS_SNAPSHOT', onBreaksSnapshot);
        socket.on('USER_BREAK', onBreak);
        socket.on('NEW_BREAK_UPDATE', onBreakUpdate);
        return () => {
            socket.off('BREAKS_SNAPSHOT', onBreaksSnapshot);
            socket.off('USER_BREAK', onBreak);
            socket.off('NEW_BREAK_UPDATE', onBreakUpdate);
            socket.off('connect', fetchActiveBreaks);
        };
    }, [socket, authToken]);

    // ── Live calls socket ─────────────────────────────────────────────
    useEffect(() => {
        if (!socket) return;
        const fetchActiveCalls = async () => {
            try {
                const authyToken = localStorage.getItem(authToken);
                const decoded = jwtDecode(authyToken);
                const r = await api.get(`/calls/${decoded.organization_id}`, {
                    headers: { Authorization: `Bearer ${authyToken}` },
                    withCredentials: true,
                });
                const m = {};
                (r.data || []).forEach(c => { m[c.callId] = c; });
                setCalls(m);
            } catch (e) { console.error('Calls fetch error:', e); }
        };
        fetchActiveCalls();
        socket.on('connect', fetchActiveCalls);

        const onCallsSnapshot = (arr) => { const m = {}; (arr || []).forEach(c => { m[c.callId] = c; }); setCalls(m); };
        const onNewCall  = (c)  => setCalls(prev => ({ ...prev, [c.callId]: c }));
        const onCallEnd  = (id) => setCalls(prev => { const u = { ...prev }; delete u[String(id)]; return u; });

        socket.on('CALLS_SNAPSHOT', onCallsSnapshot);
        socket.on('NEW_LIVE_CALL', onNewCall);
        socket.on('CALL_ENDED', onCallEnd);
        return () => {
            socket.off('CALLS_SNAPSHOT', onCallsSnapshot);
            socket.off('NEW_LIVE_CALL', onNewCall);
            socket.off('CALL_ENDED', onCallEnd);
            socket.off('connect', fetchActiveCalls);
        };
    }, [socket, authToken]);

    // ── Queue socket ──────────────────────────────────────────────────
    useEffect(() => {
        if (!socket) return;

        const fetchQueue = async () => {
            try {
                const authyToken = localStorage.getItem(authToken);
                const decoded = jwtDecode(authyToken);
                const r = await api.get(`/queue/${decoded.organization_id}`, {
                    headers: { Authorization: `Bearer ${authyToken}` },
                    withCredentials: true,
                });
                if (r.data && Array.isArray(r.data)) setQueue(r.data);
            } catch (e) { console.error('Queue fetch error:', e); }
        };

        fetchQueue();
        socket.on('connect', fetchQueue);

        const onQueueSnapshot = (data) => {
            console.log('[Queue] Snapshot received:', data?.total || 0, 'callers');
            if (data && data.channels && Array.isArray(data.channels)) {
                const seen = new Set();
                const formattedChannels = data.channels
                    .filter(ch => {
                        if (!ch.uuid || seen.has(ch.uuid)) return false;
                        seen.add(ch.uuid);
                        return true;
                    })
                    .map(ch => ({
                        uuid:         ch.uuid,
                        callId:       ch.uuid,
                        callerName:   ch.callerName || ch.cid_name || 'Unknown',
                        callerNumber: ch.callerNumber || ch.cid_num || '—',
                        queueName:    ch.queueName || (ch.dest || '').split('@')[0] || 'Default',
                        wait_seconds: ch.wait_seconds,
                        joinTime:     ch.joinTime,
                        state:        ch.state || ch.callstate || 'Waiting',
                        callstate:    ch.callstate,
                    }));
                setQueue(formattedChannels);
            } else if (data && Array.isArray(data)) {
                setQueue(data);
            }
        };

        const onQueueCallerRemove = (data) => {
            const uuid = data?.uuid || data;
            if (uuid) setQueue(prev => prev.filter(c => c.uuid !== uuid && c.callId !== uuid));
        };

        const onQueueCallerAdd = (callerData) => {
            setQueue(prev => {
                const callId = callerData.callId || callerData.uuid;
                if (!callId) return prev;
                if (prev.some(c => c.uuid === callId || c.callId === callId)) return prev;
                return [...prev, {
                    uuid:         callId,
                    callId:       callId,
                    callerName:   callerData.callerName   || 'Unknown',
                    callerNumber: callerData.callerNumber || '—',
                    queueName:    callerData.queueName    || 'Default',
                    wait_seconds: null,
                    joinTime:     callerData.joinTime     || new Date().toISOString(),
                    state:        'Waiting',
                    callstate:    null,
                }];
            });
        };

        socket.on('QUEUE_SNAPSHOT', onQueueSnapshot);
        socket.on('QUEUE_CALLER_REMOVE', onQueueCallerRemove);
        socket.on('QUEUE_CALLER_ADD', onQueueCallerAdd);

        return () => {
            socket.off('QUEUE_SNAPSHOT', onQueueSnapshot);
            socket.off('QUEUE_CALLER_REMOVE', onQueueCallerRemove);
            socket.off('QUEUE_CALLER_ADD', onQueueCallerAdd);
            socket.off('connect', fetchQueue);
        };
    }, [socket, authToken]);

    // ── Handlers ──────────────────────────────────────────────────────
    const handleSupervisorEndBreak = useCallback((breakUser) => {
        const { breakId, userId: targetUserId } = breakUser;
        if (!breakId || !socket) return;
        setEndingBreaks(prev => new Set([...prev, breakId]));
        socket.emit('DROP_BREAK', { breakId, targetUserId });
        setTimeout(() => fetchBreakDataRef.current?.(), 500);
    }, [socket]);

    const handleActiveChange = useCallback((e, breakId) => {
        const authyToken = localStorage.getItem(authToken);
        api.put(`/break/update/${breakId}`, {}, {
            headers: { Authorization: `Bearer ${authyToken}`, 'Content-Type': 'application/json' },
            withCredentials: true,
        })
            .then(() => {
                setActiveEnabled(prev => ({ ...prev, [breakId]: e.target.checked }));
                setTimeout(() => fetchBreakDataRef.current?.(), 500);
            })
            .catch(console.error);
    }, [authToken]);

    const handleDropLocal = useCallback((caller) => {
        const uuid = caller.uuid || caller.callId;
        if (!uuid) return;
        setQueue(prev => prev.filter(c => c.uuid !== uuid && c.callId !== uuid));
        if (socket) socket.emit('QUEUE_LOCAL_REMOVE', { uuid });
        console.log('[Queue] Removed from view:', uuid);
    }, [socket]);

    const paginatedPresence = onlineUsers.slice(presencePage * presenceRowsPerPage, (presencePage + 1) * presenceRowsPerPage);
    const paginatedCalls    = callList.slice(callPage * callRowsPerPage, (callPage + 1) * callRowsPerPage);
    const paginatedQueue    = queue.slice(queuePage * queueRowsPerPage, (queuePage + 1) * queueRowsPerPage);
    const activeBreakCount  = activeBreaks.length;
    const levelColor        = audioLevel > 70 ? '#ef5350' : audioLevel > 40 ? '#66bb6a' : '#42a5f5';

    // ── Render ────────────────────────────────────────────────────────
    return (
        <>
            <CssBaseline />
            <Typography variant="h5" sx={{ marginBottom: '15px' }}>Monitor</Typography>

            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                {[
                    { label: 'Online Users', value: onlineUsers.length,        color: 'success.light' },
                    { label: 'Active Calls', value: Object.keys(calls).length, color: 'primary.main'  },
                    { label: 'In Queue',     value: queue.length,              color: 'info.main'     },
                    { label: 'On Break',     value: activeBreakCount,          color: 'warning.light' },
                ].map(({ label, value, color }) => (
                    <Grid item xs={3} key={label}>
                        <Box sx={{ bgcolor: color, color: 'white', p: 2, borderRadius: 1, textAlign: 'center' }}>
                            <Typography variant="h4">{value}</Typography>
                            <Typography variant="body2">{label}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>

            {listeningCallId && (
                <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: 'rgba(25,118,210,0.06)', border: '1px solid rgba(25,118,210,0.25)', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FiberManualRecordIcon sx={{ fontSize: 10, color: 'success.main', animation: 'pulse 1.5s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.3 }, '100%': { opacity: 1 } } }} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>Monitoring</Typography>
                        <Chip label={listeningCallId} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: 11 }} />
                    </Box>
                    <Tooltip title={`Audio level: ${audioLevel}%`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {[...Array(10)].map((_, i) => (
                                <Box key={i} sx={{ width: 4, height: 14, borderRadius: 1, bgcolor: i < Math.round(audioLevel / 10) ? levelColor : 'rgba(0,0,0,0.12)', transition: 'background-color 0.1s' }} />
                            ))}
                        </Box>
                    </Tooltip>
                    {audioErrors > 3 && <Chip label={`${audioErrors} errors`} size="small" color="warning" variant="outlined" />}
                    {autoplayBlocked && (
                        <Chip 
                            label="Click to enable audio" 
                            size="small" 
                            color="warning" 
                            icon={<VolumeUpIcon />}
                            onClick={() => {
                                if (remoteAudioRef.current) {
                                    remoteAudioRef.current.play().then(() => {
                                        console.log('[Audio] Playback started after button click');
                                        setAutoplayBlocked(false);
                                    }).catch(e => console.warn('[Audio] Still blocked:', e));
                                }
                            }}
                            sx={{ cursor: 'pointer' }}
                        />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 160 }}>
                        <Tooltip title={isMuted ? 'Unmute' : 'Mute'}>
                            <IconButton size="small" onClick={handleMuteToggle}>
                                {isMuted || volume === 0 ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Slider size="small" value={isMuted ? 0 : volume} min={0} max={1} step={0.05} onChange={handleVolumeChange} sx={{ width: 90 }} />
                        <Typography variant="caption" sx={{ minWidth: 28, color: 'text.secondary' }}>
                            {isMuted ? '0%' : `${Math.round(volume * 100)}%`}
                        </Typography>
                    </Box>
                    <Tooltip title={isWhispering ? 'Release to stop' : 'Hold to whisper to agent (customer cannot hear)'}>
                        <Button
                            variant={isWhispering ? 'contained' : 'outlined'}
                            color={isWhispering ? 'warning' : 'secondary'}
                            size="small"
                            startIcon={isWhispering ? <MicIcon /> : <MicOffIcon />}
                            onMouseDown={startWhisper}
                            onMouseUp={stopWhisper}
                            onMouseLeave={stopWhisper}
                            onTouchStart={startWhisper}
                            onTouchEnd={stopWhisper}
                            sx={{ ml: 'auto', minWidth: 140, flexShrink: 0 }}
                        >
                            {isWhispering ? 'Whispering…' : 'Hold to whisper'}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Stop monitoring">
                        <IconButton size="small" color="error" onClick={stopListen}>
                            <HeadsetOffIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: '0.1px solid rgba(207,211,212,0.2)', borderRadius: '5px' }}>
                        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth" sx={{ padding: '2px' }}>
                            <Tab label={`Online (${onlineUsers.length})`} />
                            <Tab label={`Active Calls (${Object.keys(calls).length})`} />
                            <Tab label={`Queue (${queue.length})`} />
                            <Tab label={`Breaks (${activeBreakCount} active)`} />
                        </Tabs>

                        {/* ── Online tab ── */}
                        {activeTab === 0 && (
                            <>
                                <TableContainer><Table size="medium">
                                    <TableHead sx={{ bgcolor: 'background.table' }}>
                                        <TableRow>
                                            <TableCell>User</TableCell><TableCell>Extension</TableCell>
                                            <TableCell>Level</TableCell><TableCell>Online For</TableCell><TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedPresence.length === 0
                                            ? <TableRow><TableCell colSpan={5} align="center"><Typography variant="body2" color="textSecondary">No users currently online</Typography></TableCell></TableRow>
                                            : paginatedPresence.map(user => (
                                                <TableRow key={user.userId}>
                                                    <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ width: 32, height: 32, bgcolor: 'success.main' }}>{user.user_name.charAt(0).toUpperCase()}</Avatar>{user.user_name}</Box></TableCell>
                                                    <TableCell><Chip label={user.user_extension} size="small" variant="outlined" /></TableCell>
                                                    <TableCell><Chip label={user.user_level} size="small" variant="outlined" color={user.user_level === 'admin' ? 'error' : user.user_level === 'supervisor' ? 'warning' : 'default'} /></TableCell>
                                                    <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'success.main', fontWeight: 600 }}>{getTimeDifference(user.connectedAt, null)}</Typography></TableCell>
                                                    <TableCell><Chip label="Online" color="success" size="small" icon={<Box sx={{ width: 8, height: 8, bgcolor: '#06402B', borderRadius: '50%', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 } } }} />} /></TableCell>
                                                </TableRow>
                                            ))
                                        }
                                    </TableBody>
                                </Table></TableContainer>
                                <TablePagination component="div" count={onlineUsers.length} page={presencePage}
                                    onPageChange={(_, p) => setPresencePage(p)} rowsPerPage={presenceRowsPerPage}
                                    onRowsPerPageChange={e => { setPresenceRowsPerPage(parseInt(e.target.value, 10)); setPresencePage(0); }}
                                    rowsPerPageOptions={[10, 50, 100]} labelRowsPerPage="Rows per page" />
                            </>
                        )}

                        {/* ── Active Calls tab ── */}
                        {activeTab === 1 && (
                            <>
                                <TableContainer><Table size="medium">
                                    <TableHead sx={{ bgcolor: 'background.table' }}>
                                        <TableRow>
                                            <TableCell>Agent</TableCell><TableCell>Direction</TableCell>
                                            <TableCell>Call To</TableCell><TableCell>Call From</TableCell>
                                            <TableCell>Status</TableCell><TableCell>Duration</TableCell>
                                            <TableCell>Started</TableCell><TableCell align="center">Monitor</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedCalls.length === 0
                                            ? <TableRow><TableCell colSpan={8} align="center"><Typography variant="body2" color="textSecondary">No active calls</Typography></TableCell></TableRow>
                                            : paginatedCalls.map(call => {
                                                const isListening = listeningCallId === call.callId;
                                                const canListen   = audioSocketRef.current?.connected && !listeningCallId;
                                                return (
                                                    <TableRow key={call.callId} sx={isListening ? { bgcolor: 'rgba(25,118,210,0.04)' } : {}}>
                                                        <TableCell>{call.agentName}</TableCell>
                                                        <TableCell><Chip label={call.callType} size="small" color={call.callType === 'Inbound' ? 'primary' : 'secondary'} /></TableCell>
                                                        <TableCell>{call.callTo}</TableCell>
                                                        <TableCell>{call.callFrom}</TableCell>
                                                        <TableCell><Chip label="Live" color="success" size="small" icon={<Box sx={{ width: 8, height: 8, bgcolor: '#06402B', borderRadius: '50%', animation: 'pulse 2s infinite' }} />} /></TableCell>
                                                        <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'success.dark', fontWeight: 600 }}>{getTimeDifference(call.startTime, null)}</Typography></TableCell>
                                                        <TableCell>{new Date(call.startTime).toLocaleTimeString()}</TableCell>
                                                        <TableCell align="center">
                                                            {isListening ? (
                                                                <Tooltip title="Stop monitoring">
                                                                    <IconButton size="small" color="primary" onClick={stopListen} sx={{ bgcolor: 'rgba(25,118,210,0.1)', '&:hover': { bgcolor: 'rgba(25,118,210,0.2)' } }}>
                                                                        <HeadsetOffIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            ) : (
                                                                <Tooltip title={
                                                                    !audioSocketRef.current?.connected ? 'Audio relay not connected'
                                                                    : listeningCallId ? 'Already monitoring another call'
                                                                    : `Listen to ${call.agentName}`
                                                                }>
                                                                    <span>
                                                                        <IconButton size="small" color="default"
                                                                            disabled={!canListen}
                                                                            onClick={() => startListen(call)}
                                                                            sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' } }}>
                                                                            <HeadphonesIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        }
                                    </TableBody>
                                </Table></TableContainer>
                                <TablePagination component="div" count={callList.length} page={callPage}
                                    onPageChange={(_, p) => setCallPage(p)} rowsPerPage={callRowsPerPage}
                                    onRowsPerPageChange={e => { setCallRowsPerPage(parseInt(e.target.value, 10)); setCallPage(0); }}
                                    rowsPerPageOptions={[5, 10, 15]} labelRowsPerPage="Rows per page" />
                            </>
                        )}

                        {/* ── Queue tab ── */}
                        {activeTab === 2 && (
                            <>
                                <TableContainer><Table size="medium">
                                    <TableHead sx={{ bgcolor: 'background.table' }}>
                                        <TableRow>
                                            <TableCell>#</TableCell>
                                            <TableCell>Caller</TableCell>
                                            <TableCell>Number</TableCell>
                                            <TableCell>Queue</TableCell>
                                            <TableCell>Wait Time</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Remove</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedQueue.length === 0
                                            ? <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="textSecondary">No callers in queue</Typography></TableCell></TableRow>
                                            : paginatedQueue.map((caller, idx) => {
                                                const uuid = caller.uuid || caller.callId;
                                                return (
                                                    <TableRow key={uuid}>
                                                        <TableCell>
                                                            <Chip label={queuePage * queueRowsPerPage + idx + 1} size="small" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Avatar sx={{ width: 28, height: 28, bgcolor: 'info.main', fontSize: 12 }}>
                                                                    {(caller.callerName || caller.callerNumber || '?').charAt(0).toUpperCase()}
                                                                </Avatar>
                                                                <Typography variant="body2">{caller.callerName || 'Unknown'}</Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>{caller.callerNumber || '—'}</TableCell>
                                                        <TableCell>
                                                            <Chip label={caller.queueName || 'Default'} size="small" color="info" variant="outlined" />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'info.main', fontWeight: 600 }}>
                                                                {caller.wait_seconds != null
                                                                    ? formatSeconds(caller.wait_seconds)
                                                                    : getTimeDifference(caller.joinTime, null)
                                                                }
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label="Waiting"
                                                                color="info"
                                                                size="small"
                                                                icon={<Box sx={{ width: 8, height: 8, bgcolor: '#0C447C', borderRadius: '50%', animation: 'pulse 2s infinite' }} />}
                                                            />
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            <Tooltip placement="left" title={`Remove ${caller.callerName || caller.callerNumber} from view`}>
                                                                <IconButton size="small" color="default" onClick={() => handleDropLocal(caller)} sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.08)' }, transition: 'all 0.15s ease' }}>
                                                                    <CloseIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        }
                                    </TableBody>
                                </Table></TableContainer>
                                <TablePagination component="div" count={queue.length} page={queuePage}
                                    onPageChange={(_, p) => setQueuePage(p)} rowsPerPage={queueRowsPerPage}
                                    onRowsPerPageChange={e => { setQueueRowsPerPage(parseInt(e.target.value, 10)); setQueuePage(0); }}
                                    rowsPerPageOptions={[10, 25, 50]} labelRowsPerPage="Rows per page" />
                            </>
                        )}

                        {/* ── Breaks tab ── */}
                        {activeTab === 3 && (
                            <Box>
                                {activeBreaks.length > 0 && (
                                    <Box sx={{ borderBottom: '1px solid rgba(207,211,212,0.2)' }}>
                                        <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'rgba(255,152,0,0.06)' }}>
                                            <Box sx={{ width: 8, height: 8, bgcolor: 'warning.main', borderRadius: '50%', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 } } }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.main', textTransform: 'uppercase', letterSpacing: 1 }}>
                                                Currently on break — {activeBreaks.length} agent{activeBreaks.length !== 1 ? 's' : ''}
                                            </Typography>
                                        </Box>
                                        <TableContainer><Table size="small">
                                            <TableHead sx={{ bgcolor: 'background.table' }}>
                                                <TableRow>
                                                    <TableCell>Agent</TableCell><TableCell>Extension</TableCell><TableCell>Break Type</TableCell>
                                                    <TableCell>Started</TableCell><TableCell>Elapsed</TableCell><TableCell>Status</TableCell><TableCell align="center">Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {activeBreaks.map(user => {
                                                    const isEnding = endingBreaks.has(user.breakId);
                                                    return (
                                                        <TableRow key={user.userId} sx={{ bgcolor: 'rgba(255,152,0,0.04)' }}>
                                                            <TableCell><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Avatar sx={{ width: 28, height: 28, bgcolor: 'warning.main', fontSize: 12 }}>{user.user_name.charAt(0).toUpperCase()}</Avatar><Typography variant="body2">{user.user_name}</Typography></Box></TableCell>
                                                            <TableCell><Chip label={user.user_extension} size="small" variant="outlined" /></TableCell>
                                                            <TableCell><Chip label={user.breakType} size="small" color="warning" variant="outlined" /></TableCell>
                                                            <TableCell><Typography variant="body2">{new Date(user.startTime).toLocaleTimeString()}</Typography></TableCell>
                                                            <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'warning.main', fontWeight: 600 }}>{getTimeDifference(user.startTime, null)}</Typography></TableCell>
                                                            <TableCell><Chip label={isEnding ? 'Ending…' : 'On Break'} color="warning" size="small" icon={<Box sx={{ width: 8, height: 8, bgcolor: '#FF8C00', borderRadius: '50%', animation: 'pulse 2s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.4 }, '100%': { opacity: 1 } } }} />} /></TableCell>
                                                            <TableCell align="center">
                                                                <Tooltip title={`End ${user.user_name}'s break`} placement="left">
                                                                    <span>
                                                                        <IconButton size="small" color="error" disabled={isEnding} onClick={() => handleSupervisorEndBreak(user)} sx={{ '&:hover': { bgcolor: 'rgba(211,47,47,0.1)' }, opacity: isEnding ? 0.5 : 1 }}>
                                                                            <StopCircleIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </span>
                                                                </Tooltip>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table></TableContainer>
                                    </Box>
                                )}

                                <Box>
                                    <TableContainer><Table size="medium">
                                        <TableHead sx={{ bgcolor: 'background.table' }}>
                                            <TableRow>
                                                <TableCell>User</TableCell><TableCell>Break</TableCell><TableCell>Start</TableCell>
                                                <TableCell>End</TableCell><TableCell>Duration</TableCell><TableCell>Status</TableCell><TableCell>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {breakHistory.length === 0
                                                ? <TableRow><TableCell colSpan={7} align="center"><Typography variant="body2" color="textSecondary">No break records found</Typography></TableCell></TableRow>
                                                : breakHistory.map(row => {
                                                    const isActive  = !row.end_time;
                                                    const liveBreak = activeBreaks.find(b => b.breakId === row.break_id);
                                                    const duration  = isActive ? (liveBreak ? getTimeDifference(liveBreak.startTime, null) : '—') : computeHistoryDuration(row);
                                                    return (
                                                        <TableRow key={row.id} sx={isActive ? { bgcolor: 'rgba(255,152,0,0.03)' } : {}}>
                                                            <TableCell>{row.user_name}</TableCell>
                                                            <TableCell><Chip label={row.break} size="small" variant="outlined" /></TableCell>
                                                            <TableCell>{row.start_time}</TableCell>
                                                            <TableCell>{row.end_time || <Typography variant="body2" sx={{ color: 'warning.main', fontStyle: 'italic' }}>Ongoing</Typography>}</TableCell>
                                                            <TableCell><Typography variant="body2" sx={isActive ? { fontFamily: 'monospace', color: 'warning.main' } : {}}>{duration}</Typography></TableCell>
                                                            <TableCell>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                    <Chip label={isActive ? 'Active' : 'Completed'} color={isActive ? 'warning' : 'default'} size="small" icon={<Box sx={{ width: 8, height: 8, bgcolor: isActive ? '#FF8C00' : 'grey.500', borderRadius: '50%' }} />} />
                                                                    {isActive && !liveBreak && new Date(row.date) < new Date(Date.now() - 3 * 60 * 60 * 1000) && (
                                                                        <Tooltip title="Force close stale record">
                                                                            <Switch checked={activeEnabled[row.break_id] || false} onChange={e => handleActiveChange(e, row.break_id)} color="secondary" size="small" />
                                                                        </Tooltip>
                                                                    )}
                                                                </Box>
                                                            </TableCell>
                                                            <TableCell>{row.date.replace('T', ' ').replace(/\.\d+Z$/, '')}</TableCell>
                                                        </TableRow>
                                                    );
                                                })
                                            }
                                        </TableBody>
                                    </Table></TableContainer>
                                    <TablePagination component="div" count={breakTotalItems} page={breakPage}
                                        onPageChange={(_, p) => setBreakPage(p)} rowsPerPage={breakRowsPerPage}
                                        onRowsPerPageChange={e => { setBreakRowsPerPage(parseInt(e.target.value, 10)); setBreakPage(0); }}
                                        rowsPerPageOptions={[10, 50, 100]} labelRowsPerPage="Rows per page" />
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grid>
            </Grid>

            {/* Hidden audio element for listen-in playback */}
            <audio 
                id="monitor-remote-audio" 
                autoPlay 
                style={{ display: 'none' }}
                onError={(e) => console.error('[Audio] Element error:', e)}
            ></audio>
        </>
    );
};

export default Monitor;
